/**
 * Import Archive — a durable, organised repository of every historical
 * spreadsheet / CSV uploaded for import. Files are stored under consistent,
 * timestamped names (in object storage in production, so they survive deploys),
 * regardless of what they were originally called. The import parse/map/promote
 * pipeline reads from these archived batches.
 */
import { useRef, useState } from 'react';
import { useConfirm } from '../components/ui';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SAMPLE_GROUP_IMPORT_CSV, SAMPLE_GROUP_IMPORT_FILENAME, type ImportBatchRow } from '@gwc/shared';
import { importsApi, downloadSampleImportCsv, SAMPLE_IMPORT_COLUMNS } from '../api/imports';
import { ApiError } from '../lib/api';

function formatBytes(n: number | null): string {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) +
    ' · ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** The group results sample is built in the browser, like the scorecard one. */
function downloadSampleGroupCsv(): void {
  const blob = new Blob([SAMPLE_GROUP_IMPORT_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = SAMPLE_GROUP_IMPORT_FILENAME;
  anchor.click();
  URL.revokeObjectURL(url);
}

const STATUS_STYLE: Record<string, string> = {
  uploaded: 'border-gold-600/30 text-gold-200',
  processing: 'border-gold-600/30 text-gold-200',
  completed: 'border-emerald-600/30 text-emerald-300',
  failed: 'border-wine-500/40 text-wine-300',
};

export function Imports() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({ queryKey: ['imports'], queryFn: importsApi.list });

  const upload = useMutation({
    mutationFn: (file: File) => importsApi.upload(file),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['imports'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Upload failed'),
    onSettled: () => {
      if (fileRef.current) fileRef.current.value = '';
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => importsApi.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['imports'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not delete that file'),
  });

  const items = list.data ?? [];

  return (
    <div>
      <p className="text-xs uppercase tracking-[0.3em] text-gold-400">Records</p>
      <h1 className="mt-2 font-display text-3xl text-cream-50">Import Archive</h1>
      <p className="mt-1 max-w-2xl text-sm text-cream-300/70">
        Upload the club's historical spreadsheets and CSVs here. Every file is stored safely under a
        consistent name and kept as a permanent record of past imports — nothing is lost, even if the
        original file was named something different each time.
      </p>

      <div className="card mt-6 border-gold-600/25 bg-ink-900/40">
        <h2 className="text-sm uppercase tracking-wide text-gold-400">Import template</h2>
        <p className="mt-2 text-sm text-cream-300/80">
          Two layouts work, in .csv or Excel. Column names can vary in your own files — the import
          mapper matches them to these fields.
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="rounded border border-ink-800 p-4">
            <h3 className="text-sm text-cream-50">Member scorecards</h3>
            <p className="mt-1 text-xs text-cream-300/70">
              Each row is one member&apos;s score for one wine at one tasting. The group&apos;s
              score and ranking are worked out from the cards.
            </p>
            <button type="button" onClick={downloadSampleImportCsv} className="btn-primary mt-3 text-sm">
              Download sample CSV
            </button>
          </div>
          <div className="rounded border border-ink-800 p-4">
            <h3 className="text-sm text-cream-50">Group results only</h3>
            <p className="mt-1 text-xs text-cream-300/70">
              One row per wine, with no member names: the group score, average and where it
              finished, the way the results spreadsheet records it. Wines can be lettered (A, B…) or
              numbered (1, 2…); places like &ldquo;11th&rdquo; or &ldquo;T3&rdquo; are understood.
            </p>
            <button type="button" onClick={downloadSampleGroupCsv} className="btn-ghost mt-3 text-sm">
              Download group results sample
            </button>
          </div>
        </div>
        <p className="mt-4 text-xs leading-relaxed text-cream-300/50">
          Scorecard columns: {SAMPLE_IMPORT_COLUMNS.join(', ')}. Scores use the club scorecard —
          Sight/Nose (1–3), Taste (1–3), Overall (1–4). Group results columns: Tasting, Year, Wine #,
          the wine&apos;s details, Total Points, Avg, Votes and Place. A sheet can mix both: a row
          without a member is read as the wine&apos;s group result.
        </p>
      </div>

      <div className="card mt-6">
        <h2 className="text-sm uppercase tracking-wide text-gold-400">Upload a file</h2>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => {
              setError(null);
              const file = e.target.files?.[0];
              if (file) upload.mutate(file);
            }}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={upload.isPending}
            className="btn-primary"
          >
            {upload.isPending ? 'Uploading…' : 'Choose file'}
          </button>
          <span className="text-xs text-cream-300/50">.csv, .xls or .xlsx · up to 25 MB</span>
        </div>
        {error && <p className="mt-3 text-sm text-wine-300">{error}</p>}
      </div>

      <div className="mt-6">
        {list.isLoading ? (
          <p className="text-sm text-cream-300/60">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-cream-300/60">No imports uploaded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-ink-800 text-xs uppercase tracking-wide text-cream-300/50">
                  <th className="py-2 pr-4 font-medium">File</th>
                  <th className="py-2 pr-4 font-medium">Uploaded</th>
                  <th className="py-2 pr-4 font-medium">By</th>
                  <th className="py-2 pr-4 font-medium">Size</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {items.map((it: ImportBatchRow) => (
                  <tr key={it.id} className="border-b border-ink-900/60 align-middle">
                    <td className="py-3 pr-4">
                      <span className="text-cream-100">{it.filename}</span>
                      <span className="ml-2 rounded bg-ink-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-cream-300/60">
                        {it.format ?? '?'}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-cream-300/70">{formatDate(it.createdAt)}</td>
                    <td className="py-3 pr-4 text-cream-300/70">{it.uploadedByName ?? '—'}</td>
                    <td className="py-3 pr-4 text-cream-300/70">{formatBytes(it.sizeBytes)}</td>
                    <td className="py-3 pr-4">
                      <span
                        className={`rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${STATUS_STYLE[it.status] ?? 'border-ink-800 text-cream-300/60'}`}
                      >
                        {it.status}
                      </span>
                    </td>
                    <td className="py-3 text-right whitespace-nowrap">
                      <Link to={`/imports/${it.id}`} className="text-xs text-gold-400 hover:text-gold-200">
                        Process
                      </Link>
                      <a
                        href={importsApi.downloadUrl(it.id)}
                        className="text-xs text-gold-400 hover:text-gold-200"
                      >
                        Download
                      </a>
                      <button
                        type="button"
                        onClick={async () => {
                          const ok = await confirm({
                            title: `Remove “${it.filename}”?`,
                            description: 'This deletes the stored file from the archive.',
                            confirmLabel: 'Remove file',
                            destructive: true,
                          });
                          if (ok) remove.mutate(it.id);
                        }}
                        className="ml-3 text-xs text-cream-300/40 hover:text-danger"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
